import { Mote } from "./mote";
import { LanguageMap, Language } from "./language";
import { GenreTourTag, GeoTourTag, OtherTourTag, TourTag } from "./tour-tags";
import { TourTagLabel } from "./tour-tag-label";
import { LanguageService } from "../providers/language.service";

export type SearchItem = string | boolean | number;

export interface FilterOption {
    searchItem: SearchItem,
    label: string,
    isChosen: boolean
};

export abstract class FilterCategory {
    public title: string;
    public options: FilterOption[];
    public mutuallyExclusive: boolean;
    public abstract filterTours(tours: Mote[], locale: string): Mote[];

    public getSearchItems(): SearchItem[] {
        return this.options.reduce((filtered, option) => {
            if(option.isChosen) filtered.push(option.searchItem);
            return filtered
        }, []);
    }

    public filterByTags(tours: Mote[], targetTags: string[]): Mote[] {
        return tours.filter((tour) => {
            let tags = tour.tags ? tour.tags.split(',') : [];
            return tags.some((tag) => targetTags.includes(tag.trim()));
        });
    }

    public splitByTag(tours: Mote[], targetTag: TourTag): { hasTarget: Mote[], lacksTarget: Mote[] } {
        let hasTarget = [];
        let lacksTarget = [];
        tours.forEach((tour) => {
            let tags = tour.tags ? tour.tags.split(',') : [];
            tags.some((tag) => tag.trim() == targetTag) ? hasTarget.push(tour) : lacksTarget.push(tour);
        })
        return { hasTarget: hasTarget, lacksTarget: lacksTarget };
    }
}

export class LangFilter extends FilterCategory {
    public title = $localize`Tour language`;
    public options = Object.keys(Language).map((lang) => { return { searchItem: lang, label: LanguageMap[lang].label, isChosen: false } });
    public mutuallyExclusive = false;

    public filterTours(tours: Mote[], locale: string): Mote[] {
        // Extract chosen language options
        let chosenLangs = this.getSearchItems();
        // Find tours matching chosen languages
        let filteredTours = chosenLangs.length ? tours.filter((tour) => chosenLangs.includes(tour.language)) : tours;
        // Remove duplicate tours by extracting the preferred translation for each collection of translations
        return LanguageService.filterMotesByLocale(filteredTours, locale);
    }
}

export class GeoFilter extends FilterCategory {
    public title = $localize`Area`;
    public options = Object.keys(GeoTourTag).map((geo) => { return { searchItem: geo, label: TourTagLabel[GeoTourTag[geo]], isChosen: false } });
    public mutuallyExclusive = false;

    public filterTours(tours: Mote[]): Mote[] {
        // Extract chosen geo options
        let chosenGeos = this.getSearchItems();
        if(!chosenGeos.length) return tours;
        return this.filterByTags(tours, chosenGeos as string[]);
    }
}

export class GenreFilter extends FilterCategory {
    public title = $localize`Genre`;
    public options = Object.keys(GenreTourTag).map((genre) => { return { searchItem: genre, label: TourTagLabel[GenreTourTag[genre]], isChosen: false } });
    public mutuallyExclusive = false;

    public filterTours(tours: Mote[]): Mote[] {
        // Extract chosen genre options
        let chosenGenre = this.getSearchItems();
        if(!chosenGenre.length) return tours;
        return this.filterByTags(tours, chosenGenre as string[]);
    }
}

export class FoodFilter extends FilterCategory {
    public title = $localize`Food & drinks`;
    public options = [{ searchItem: OtherTourTag.food_drinks, label: $localize`Included`, isChosen: false }];
    public mutuallyExclusive = false;

    public filterTours(tours: Mote[]): Mote[] {
        // Extract chosen food/drinks options
        let chosenFoods = this.getSearchItems();
        if(!chosenFoods.length) return tours;
        return this.filterByTags(tours, chosenFoods as string[]);
    }
}

export class PriceFilter extends FilterCategory {
    public title = $localize`Max price`;
    public options = [{ searchItem: 0, label: $localize`Free`, isChosen: false }, { searchItem: 100, label: '100 ' + $localize`DKK`, isChosen: false }, { searchItem: 200, label: '200 ' + $localize`DKK`, isChosen: false }, { searchItem: 300, label: '300 ' + $localize`DKK`, isChosen: false }];
    public mutuallyExclusive = true;

    public filterTours(tours: Mote[]): Mote[] {
        // Extract chosen prices
        let chosenPrices = this.getSearchItems();
        if(!chosenPrices.length) return tours;
        // OBS: We have to assume chosenPrices is a numbers array in the current design :/
        return tours.filter((tour) => (chosenPrices as number[]).some((price) => tour.price <= price));
    }
}
